import React, { useState, useCallback } from "react"
import { observer } from "mobx-react"
import { Box, Typography, Grid, Tabs, Tab, Paper, Stack } from "@mui/material"

import TestConfigurationList from "./components/TestConfigurationList"
import RulesEditorWithCRUD from "./components/RulesEditorWithCRUD"
import TestRunner from "./components/TestRunner"
import TestResults from "./components/TestResults"

import type { adminv3_ChatbotTestConfigurationResponse } from "src/api/models/adminv3_ChatbotTestConfigurationResponse"

interface ITabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: ITabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`chatbot-testing-tabpanel-${index}`}
            aria-labelledby={`chatbot-testing-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    )
}

export const ChatbotTestingPage: React.FC = observer(() => {
    const [selectedConfiguration, setSelectedConfiguration] =
        useState<adminv3_ChatbotTestConfigurationResponse | null>(null)
    const [refreshTrigger, setRefreshTrigger] = useState(0)
    const [activeTab, setActiveTab] = useState(0)

    const handleConfigurationChange = useCallback(
        (config: adminv3_ChatbotTestConfigurationResponse | null) => {
            setSelectedConfiguration(config)
        },
        [],
    )

    const handleConfigurationsUpdate = useCallback(() => {
        setRefreshTrigger((prev) => prev + 1)
    }, [])

    const handleTabChange = useCallback(
        (event: React.SyntheticEvent, newValue: number) => {
            setActiveTab(newValue)
        },
        [],
    )

    return (
        <Stack spacing={2.5}>
            <Box>
                <Typography variant="h4" component="h1" gutterBottom>
                    Chatbot Testing
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    Create, manage, and run automated tests for your chatbot
                    configurations
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Left Panel - Configuration List */}
                <Grid item xs={12} lg={3}>
                    <TestConfigurationList
                        selectedConfiguration={selectedConfiguration}
                        onConfigurationSelect={handleConfigurationChange}
                        refreshTrigger={refreshTrigger}
                        onRefreshTriggerChange={handleConfigurationsUpdate}
                    />
                </Grid>

                {/* Right Panel - Tabs */}
                <Grid item xs={12} lg={9}>
                    <Paper sx={{ width: "100%" }}>
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                            <Tabs
                                value={activeTab}
                                onChange={handleTabChange}
                                aria-label="chatbot testing tabs"
                            >
                                <Tab label="Configuration Editor" />
                                <Tab label="Test Runner" />
                                <Tab label="Test Results" />
                            </Tabs>
                        </Box>

                        <TabPanel value={activeTab} index={0}>
                            <RulesEditorWithCRUD
                                selectedConfiguration={selectedConfiguration}
                                onConfigurationChange={
                                    handleConfigurationChange
                                }
                                onConfigurationsUpdate={
                                    handleConfigurationsUpdate
                                }
                            />
                        </TabPanel>

                        <TabPanel value={activeTab} index={1}>
                            <TestRunner
                                selectedConfiguration={selectedConfiguration}
                            />
                        </TabPanel>

                        <TabPanel value={activeTab} index={2}>
                            <TestResults
                                selectedConfiguration={selectedConfiguration}
                                refreshTrigger={refreshTrigger}
                            />
                        </TabPanel>
                    </Paper>
                </Grid>
            </Grid>
        </Stack>
    )
})

export { ChatbotTestingPage as ChatbotTesting }
export default ChatbotTestingPage
